import { RigModel, dots, flipY, hex, type Animator, type Color, type Palette, type PathOp, type Pose, type Shape } from "./Rig";
import { pixelModel } from "./pixelArt";

/**
 * Skyfish, side on, nose toward +x, two kinds (by the school's seed):
 * - minnow: plump and deep-sea looking, one huge lamp of an eye, a lure on
 *   a stalk over its nose, lit scales along its flank, a fan tail;
 * - ray: a flat kite of a fish, wing edges rippling, lit wingtips and a
 *   pair of lit eyes on its back, a long whip of a tail.
 * Each school is one of three colourways. Fins glow faintly and are
 * see-through, so they shine over what's behind them.
 */

export type FishVariant = "minnow" | "ray";
export const FISH_VARIANTS: readonly FishVariant[] = ["minnow", "ray"];

/** Which kind a school is, from a 0..1 number fixed by its seed. */
export function fishVariant(h: number): FishVariant {
  return h < 0.5 ? "minnow" : "ray";
}

/** How big each kind is drawn, against a fish's size. */
export const FISH_SCALE: Record<FishVariant, number> = { minnow: 1, ray: 1.25 };

export interface FishLook {
  back: Color;
  body: Color;
  belly: Color;
  glow: Color;
  fins: Color;
}

/** A school's colours: deep blue, violet or gold. */
export const FISH_LOOKS: readonly FishLook[] = [
  { back: hex(0x13285a), body: hex(0x2a5f9e), belly: hex(0x86d0e8), glow: [0.5, 2.2, 2.4], fins: [0.15, 0.6, 0.85] },
  { back: hex(0x241248), body: hex(0x4e3492), belly: hex(0xc0a4e8), glow: [1.9, 0.8, 2.4], fins: [0.55, 0.25, 0.85] },
  { back: hex(0x33200e), body: hex(0x8a5a22), belly: hex(0xf0d49a), glow: [2.4, 1.6, 0.5], fins: [0.8, 0.45, 0.12] },
];

export function fishLook(h: number): FishLook {
  return FISH_LOOKS[Math.min(FISH_LOOKS.length - 1, Math.floor(h * FISH_LOOKS.length))]!;
}

export function fishPalette(look: FishLook): Palette {
  return { ...look, eye: [0.07, 0.05, 0.03] };
}

const GLOW = ["glow", "fins"];

export const MINNOW = pixelModel({
  parts: [
    { name: "tail", at: [-0.9, 0], z: 0, moves: true, shapes: [{ color: "fins", alpha: 0.8, form: { kind: "path", ops: [["M", 0, 0.15], ["Q", -0.7, 0.9, -1.1, 0.7], ["Q", -0.8, 0.1, -1.1, -0.7], ["Q", -0.7, -0.9, 0, -0.15], ["Z"]] } }] },
    {
      name: "body",
      at: [0, 0],
      z: 1,
      shapes: [
        { color: "body", form: { kind: "disc", r: 1.15, ry: 0.72 } },
        { color: "back", form: { kind: "path", ops: [["M", 1.0, 0.35], ["C", 0.4, 0.85, -0.6, 0.85, -1.1, 0.2], ["C", -0.5, 0.45, 0.4, 0.5, 1.0, 0.35], ["Z"]] } },
        { color: "belly", form: { kind: "path", ops: [["M", 0.9, -0.4], ["C", 0.3, -0.75, -0.6, -0.7, -1.0, -0.3], ["C", -0.5, -0.35, 0.3, -0.45, 0.9, -0.4], ["Z"]] } },
        { color: "back", form: { kind: "stroke", width: 0.04, ops: [["M", 1.1, -0.1], ["Q", 0.9, -0.3, 0.6, -0.3]] } },
      ],
    },
    // Lit scales in a curve along the flank, and the lamp of an eye.
    { name: "lights", at: [0, 0], z: 2, shapes: [{ color: "glow", form: dots([0, 1, 2, 3, 4].map((i) => [0.7 - i * 0.38, -0.12 - Math.sin(i * 0.8) * 0.12] as const), 0.04) }, { color: "glow", at: [0.62, 0.12], form: { kind: "disc", r: 0.26 } }] },
    { name: "pupil", at: [0, 0], z: 3, shapes: [{ color: "eye", at: [0.68, 0.14], form: { kind: "disc", r: 0.11, ry: 0.13 } }] },
    { name: "fin", at: [0.1, -0.5], z: 4, moves: true, shapes: [{ color: "fins", alpha: 0.8, form: { kind: "path", ops: [["M", 0, 0], ["Q", -0.3, -0.5, -0.7, -0.7], ["Q", -0.3, -0.3, 0.1, -0.15], ["Z"]] } }] },
    // The lure, on a stalk from the brow.
    { name: "lure", at: [0.5, 0.6], z: 5, moves: true, shapes: [{ color: "body", form: { kind: "stroke", width: 0.05, ops: [["M", 0, 0], ["Q", 0.5, 0.7, 1.0, 0.4]] } }, { color: "glow", at: [1.0, 0.4], form: { kind: "disc", r: 0.11 } }] },
  ],
  glow: GLOW,
});

const WING: PathOp[] = [["M", 1.2, 0], ["Q", 0.6, 0.55, -0.2, 0.95], ["Q", -0.6, 0.6, -1.1, 0.1], ["Z"]];
const WING_LINES: PathOp[] = [0, 1, 2, 3].flatMap((i): PathOp[] => {
  const x = 0.8 - i * 0.45;
  return [["M", x, 0.15], ["Q", x - 0.4, 0.5, x - 0.7, 0.75 - i * 0.12]];
});
/** A wing from the spine, its lines fading out across it and a lit tip; `lower` is the same turned over. */
const wing = (lower: boolean): Shape[] => [
  { color: "body", form: { kind: "path", ops: lower ? flipY(WING) : WING } },
  { color: "belly", alpha: 0.5, form: { kind: "stroke", width: 0.03, ops: lower ? flipY(WING_LINES) : WING_LINES } },
  { color: "glow", at: [-0.25, lower ? -0.9 : 0.9], form: { kind: "disc", r: 0.06 } },
];

export const RAY = pixelModel({
  parts: [
    { name: "whip", at: [-1.1, 0], z: 0, moves: true, shapes: [{ color: "fins", alpha: 0.8, form: { kind: "stroke", width: 0.05, ops: [["M", 0, 0], ["Q", -0.7, 0.1, -1.5, 0.3]] } }] },
    { name: "upperWing", at: [0, 0], z: 1, moves: true, shapes: wing(false) },
    { name: "lowerWing", at: [0, 0], z: 1, moves: true, shapes: wing(true) },
    { name: "spine", at: [0, 0], z: 2, shapes: [{ color: "back", form: { kind: "path", ops: [["M", 1.15, 0], ["Q", 0, 0.28, -1.05, 0.08], ["L", -1.05, -0.08], ["Q", 0, -0.28, 1.15, 0], ["Z"]] } }] },
    { name: "eyes", at: [0, 0], z: 3, shapes: [{ color: "glow", form: dots([[0.7, 0.2], [0.7, -0.2]], 0.08) }] },
  ],
  glow: GLOW,
}, 32 * FISH_SCALE.ray);

export function fishModel(variant: FishVariant): RigModel {
  return variant === "minnow" ? MINNOW : RAY;
}

export interface Swim {
  /** How frightened the school is (0 calm .. 1 scattering): everything beats faster. */
  scatter: number;
}

/** One fish's swimming: tail and fin beating, the lure swaying; a ray's wings rippling and its whip of a tail trailing. */
export class FishAnimator implements Animator<Swim> {
  private t = 0;

  constructor(private readonly phase: number) {}

  step(dt: number, s: Swim): Pose {
    this.t += dt;
    const t = this.t;
    const p = this.phase;
    const beat = 9 + s.scatter * 9;
    const ripple = Math.sin(t * beat * 0.6 + p) * 0.1;
    return {
      tail: { rot: Math.sin(t * beat + p) * (0.25 + s.scatter * 0.15) },
      fin: { rot: Math.sin(t * beat * 0.7 + p) * 0.4 },
      lure: { rot: Math.sin(t * 2.5 + p) * 0.15 },
      upperWing: { rot: ripple },
      lowerWing: { rot: -ripple },
      whip: { rot: Math.sin(t * 3 + p) * 0.25 },
    };
  }
}
